import {
  Box,
  Link as MuiLink,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";

import AvatarStack from "components/common/AvatarStack";
import { CALIBRATE_COLORS } from "components/hoc/Theme";
import type {
  SecureProjectColumn,
  SecureProjectRow,
} from "features/secureUploads/secureUploads";
import useResponsive from "hooks/useResponsive";

import ProjectStatusChip from "./ProjectStatusChip";
import RowMenu, { type RowMenuItem } from "./RowMenu";
import { activityDate } from "./clientListModel";
import { ProjectIcon } from "./icons";
import type { ProjectAction } from "./ProjectDialogs";

export interface ProjectSort {
  column: SecureProjectColumn;
  direction: "asc" | "desc";
}

/** The menu of a project row; on a closed project only reopening, access and
 * deleting are left (nothing else changes inside it). */
export function projectMenu(
  project: Pick<SecureProjectRow, "status">,
): {
  kind: ProjectAction["kind"] | "open";
  label: string;
  danger?: boolean;
  dividerBefore?: boolean;
}[] {
  if (project.status === "closed")
    return [
      { kind: "open", label: "Open Project" },
      { kind: "reopen", label: "Reopen Project" },
      { kind: "access", label: "Manage Access" },
      {
        kind: "delete",
        label: "Delete Project",
        danger: true,
        dividerBefore: true,
      },
    ];
  return [
    { kind: "open", label: "Open Project" },
    { kind: "edit", label: "Edit Project" },
    { kind: "access", label: "Manage Access" },
    { kind: "close", label: "Close Project", dividerBefore: true },
    { kind: "delete", label: "Delete Project", danger: true },
  ];
}

const COLUMNS: {
  key: SecureProjectColumn | "contributors";
  label: string;
  sortable: boolean;
  align: "left" | "center";
}[] = [
  { key: "name", label: "Project Name", sortable: true, align: "left" },
  { key: "status", label: "Status", sortable: true, align: "center" },
  { key: "files", label: "Files", sortable: true, align: "center" },
  {
    key: "contributors",
    label: "Contributors",
    sortable: false,
    align: "center",
  },
  {
    key: "latest_activity",
    label: "Latest Activity",
    sortable: true,
    align: "center",
  },
];

interface ProjectTableProps {
  projects: SecureProjectRow[];
  loading: boolean;
  sort: ProjectSort | null;
  onSort: (column: SecureProjectColumn) => void;
  empty: string;
  projectPath: (project: SecureProjectRow) => string;
  /** Staff who organize projects get the row menu; client users do not. */
  canOrganize: boolean;
  /** Client View: the menu's writes stay visible but off. */
  readOnly: boolean;
  onOpen: (project: SecureProjectRow) => void;
  onAction: (action: ProjectAction) => void;
}

export default function ProjectTable({
  projects,
  loading,
  sort,
  onSort,
  empty,
  projectPath,
  canOrganize,
  readOnly,
  onOpen,
  onAction,
}: ProjectTableProps) {
  const { isCardMode } = useResponsive();

  const menu = (project: SecureProjectRow) => {
    if (!canOrganize) return null;
    const items: RowMenuItem[] = projectMenu(project).map((item) => ({
      key: item.kind,
      label: item.label,
      danger: item.danger,
      dividerBefore: item.dividerBefore,
      readOnly: item.kind !== "open" && readOnly,
      onClick: () =>
        item.kind === "open"
          ? onOpen(project)
          : onAction({ kind: item.kind, project } as ProjectAction),
    }));
    return <RowMenu label={`Actions for ${project.name}`} items={items} />;
  };

  const name = (project: SecureProjectRow) => (
    <MuiLink
      component={Link}
      to={projectPath(project)}
      underline="hover"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        fontWeight: 600,
        color: CALIBRATE_COLORS.iconContainerFill,
        minWidth: 0,
      }}
    >
      <ProjectIcon sx={{ fontSize: 20, flexShrink: 0 }} />
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {project.name}
      </Box>
    </MuiLink>
  );

  if (isCardMode)
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1.5 }}>
        {loading ? (
          <Skeleton variant="rounded" height={88} sx={{ my: 1.5 }} />
        ) : projects.length === 0 ? (
          <Typography
            color="text.secondary"
            sx={{ py: 3, textAlign: "center" }}
          >
            {empty}
          </Typography>
        ) : (
          projects.map((project) => (
            <Box
              key={project.id}
              sx={{
                py: 1.25,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: "grid", gap: 0.75 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {name(project)}
                  <ProjectStatusChip status={project.status} />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    fontSize: 12,
                    color: "text.secondary",
                  }}
                >
                  <span>Files: {project.files}</span>
                  <AvatarStack
                    people={project.contributors}
                    size={22}
                    empty="-"
                  />
                  <span>{activityDate(project.latest_activity)}</span>
                </Box>
              </Box>
              {menu(project)}
            </Box>
          ))
        )}
      </Box>
    );

  return (
    <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <Table stickyHeader size="small" aria-label="Projects">
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell
                key={column.key}
                align={column.align}
                sx={{ bgcolor: "grey.100", whiteSpace: "nowrap" }}
              >
                {column.sortable ? (
                  <TableSortLabel
                    active={sort?.column === column.key}
                    direction={
                      sort?.column === column.key ? sort.direction : "asc"
                    }
                    onClick={() => onSort(column.key as SecureProjectColumn)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
            <TableCell sx={{ bgcolor: "grey.100", width: 48 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? Array.from({ length: 4 }, (_, index) => (
                <TableRow key={index}>
                  {COLUMNS.map((column) => (
                    <TableCell key={column.key}>
                      <Skeleton />
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              ))
            : projects.map((project) => (
                <TableRow key={project.id} hover>
                  <TableCell sx={{ maxWidth: 380 }}>{name(project)}</TableCell>
                  <TableCell align="center">
                    <ProjectStatusChip status={project.status} />
                  </TableCell>
                  <TableCell align="center">{project.files}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: "inline-flex" }}>
                      <AvatarStack
                        people={project.contributors}
                        size={26}
                        empty="-"
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {activityDate(project.latest_activity)}
                  </TableCell>
                  <TableCell align="right">{menu(project)}</TableCell>
                </TableRow>
              ))}
          {!loading && projects.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length + 1} align="center">
                <Typography color="text.secondary" sx={{ py: 3 }}>
                  {empty}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
